<template lang="html">
  <div class="cart flex flex-col gap-4 sm:gap-6">
    <div class="cart__header">
      <h1 class="text-2xl font-bold tracking-tight text-highlighted sm:text-3xl">
        Корзина
        <span
          v-if="totalLength"
          class="ms-2 text-xl font-semibold text-muted sm:text-2xl"
        >{{ totalLength }}</span>
      </h1>
    </div>

    <UAlert
      v-if="isAnonymous && cartItems.length"
      color="info"
      icon="i-lucide-triangle-alert"
      variant="soft"
      title="Войдите, чтобы применять акции и отслеживать заказ"
      :actions="[
        {
          label: 'Войти',
          onClick: () => openAuth(),
        },
      ]"
    />

    <NonePanel
      v-if="!cartItems.length"
      key="none"
      class="border-0!"
    >
      <template #text>
        <div class="flex flex-col items-center gap-3 text-center">
          <p class="text-xl font-bold">
            Корзина пуста
          </p>
          <p class="max-w-md text-sm text-muted">
            Добавьте товары из каталога, чтобы оформить заказ.
          </p>
          <div class="mt-2 flex flex-wrap items-center justify-center gap-3">
            <UButton
              to="/catalog"
              color="primary"
            >
              Перейти в каталог
            </UButton>
            <UButton
              to="/"
              variant="outline"
              color="neutral"
            >
              На главную
            </UButton>
          </div>
        </div>
      </template>
    </NonePanel>

    <div
      v-else
      class="relative grid gap-6 lg:grid-cols-3 lg:gap-8"
    >
      <AppLoader
        v-if="isLoading"
        key="loader"
        class="absolute top-1/2 left-1/2 z-20 h-20 w-20 -translate-1/2 text-tertiary-500"
      />

      <div class="flex min-w-0 flex-col gap-4 lg:col-span-2">
        <div
          v-if="availableCartItems.length"
          class="flex flex-wrap items-center justify-start gap-1 self-start rounded-md border border-default bg-default p-1"
        >
          <UButton
            variant="ghost"
            color="neutral"
            size="sm"
            @click="toggleSelectAll"
          >
            {{ allSelected ? 'Снять выбор' : 'Выбрать всё' }}
          </UButton>

          <UButton
            v-if="selectedProductIds.length"
            variant="soft"
            color="error"
            size="sm"
            icon="i-lucide-trash-2"
            :loading="checkIsLoading('bulk-remove')"
            @click="handleBulkRemove"
          >
            Удалить выбранные
          </UButton>
        </div>

        <CartList
          selectable
          :value="availableCartItems"
          :attributes-meta="store.cart?.attributesMeta"
          :selected="selectedProductIds"
          :line-unit-prices="lineUnitPriceByProductId"
          @remove="handleRemove"
          @update:selected="handleSelectedChange"
        />

        <div v-if="unavailableCartItems.length" class="flex flex-col gap-2">
          <div class="text-sm font-bold text-muted">
            Нет в наличии ({{ unavailableCartItems.length }})
          </div>
          <CartList
            :value="unavailableCartItems"
            :attributes-meta="store.cart?.attributesMeta"
            show-notify
            @remove="handleRemove"
            @toggle-notify="handleToggleNotify"
          />
        </div>

        <div
          class="flex flex-col gap-3 rounded-md border border-default bg-default p-3 sm:p-4 lg:hidden"
        >
          <div class="text-xs font-medium tracking-wide text-muted uppercase">
            Скидки и оплата
          </div>

          <PromoCodeInput
            v-model="inputCode"
            :active-promo-code="activePromoCode"
            :error="promoCodeError"
            :loading="promoLoading"
            :has-saved-promo-without-selection="hasSavedPromoWithoutSelection"
            @apply="handleApplyPromo"
            @clear="handleClearPromo"
          />

          <PointsRedemptionInput
            v-model="pointsToSpend"
            :wallet-balance="pricing?.walletBalance ?? 0"
            :max-points-redeemable="pricing?.maxPointsRedeemable ?? 0"
          />

          <PromotionStatusList
            :applied="pricing?.appliedPromotions ?? []"
            :rejected="pricing?.rejectedPromotions ?? []"
            :active-promo-code="activePromoCode"
          />
        </div>
      </div>

      <div class="hidden lg:block">
        <UCard class="sticky top-18 rounded-md">
          <PromoCodeInput
            v-model="inputCode"
            :active-promo-code="activePromoCode"
            :error="promoCodeError"
            :loading="promoLoading"
            :has-saved-promo-without-selection="hasSavedPromoWithoutSelection"
            class="mb-4"
            @apply="handleApplyPromo"
            @clear="handleClearPromo"
          />

          <PointsRedemptionInput
            v-model="pointsToSpend"
            :wallet-balance="pricing?.walletBalance ?? 0"
            :max-points-redeemable="pricing?.maxPointsRedeemable ?? 0"
            class="mb-4"
          />

          <div class="flex flex-col gap-1.5 border-t border-default pt-3 text-sm">
            <div class="flex justify-between text-muted">
              <span>Выбрано</span>
              <span>{{ selectedProductIds.length }} из {{ availableCartItems.length }}</span>
            </div>

            <div v-if="showDiscount" class="flex justify-between">
              <span class="text-muted">Сумма товаров:</span>
              <span>{{ $f.currency(pricing?.cartBase ?? fallbackCost) }}</span>
            </div>

            <div v-if="showDiscount" class="flex justify-between text-success">
              <span>Скидка:</span>
              <span>−{{ $f.currency(pricing!.discountTotal) }}</span>
            </div>

            <div v-if="showPointsApplied" class="flex justify-between text-success">
              <span>Баллы:</span>
              <span>−{{ $f.currency(pricing!.pointsApplied) }}</span>
            </div>

            <div v-if="showCashbackAccrual" class="flex justify-between text-info">
              <span>Начислим кэшбэк:</span>
              <span>+{{ $f.currency(pricing!.cashbackAccrualTotal) }}</span>
            </div>

            <div class="flex justify-between pt-1 text-base font-bold">
              <span>Итого:</span>
              <span>{{ $f.currency(selectedTotal) }}</span>
            </div>
          </div>

          <p class="mt-2 text-sm text-muted">
            Доставка рассчитается на следующем шаге
          </p>

          <UButton
            block
            size="lg"
            class="mt-4"
            :disabled="!canCheckout"
            @click="handleCheckout"
          >
            Оформить заказ ({{ selectedProductIds.length }})
          </UButton>

          <PromotionStatusList
            :applied="pricing?.appliedPromotions ?? []"
            :rejected="pricing?.rejectedPromotions ?? []"
            :active-promo-code="activePromoCode"
            class="mt-4 border-t border-default pt-3"
          />
        </UCard>
      </div>
    </div>

    <div
      v-if="cartItems.length"
      key="mobile-bar"
      class="cart__mobile-bar sticky bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] z-10 flex items-center justify-between gap-3 rounded-md border border-default bg-default px-4 py-3 lg:hidden"
    >
      <div class="flex flex-col leading-tight">
        <span class="text-xs text-muted">Итого ({{ selectedProductIds.length }})</span>
        <span class="text-base font-bold tabular-nums">{{ $f.currency(selectedTotal) }}</span>
      </div>

      <UButton
        size="lg"
        :disabled="!canCheckout"
        @click="handleCheckout"
      >
        Оформить
      </UButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCartModule } from '../../composables/useCartModule'
import { useCartSelection } from '../../composables/useCartSelection'
import CartList from '../Info/CartList.vue'

import { useEcommerce } from '#Analytics/composables/useEcommerce'
import { mapCatalogProduct } from '#Analytics/utils/ecommerceMapper'
import PointsRedemptionInput from '#Promotion/components/PointsRedemptionInput.vue'
import PromoCodeInput from '#Promotion/components/PromoCodeInput.vue'
import PromotionStatusList from '#Promotion/components/PromotionStatusList.vue'
import type { CartPricingLineInput } from '#Promotion/composables/useCartPricing'
import { useCartPricing } from '#Promotion/composables/useCartPricing'
import { usePromoCode } from '#Promotion/composables/usePromoCode'
import AppLoader from '~/app/components/Loader/AppLoader.vue'
import NonePanel from '~/app/components/None/NonePanel.vue'

const { store } = useCartModule()
const { removeFromCart, toggleNotify } = useCart()
const { send, isLoading, checkIsLoading } = useRequest()
const {
  routing: orderRouting,
  support: orderSupport,
  service: orderService,
} = useOrder()
const { store: authStore } = useAuth()
const { openAuth } = useAuthInteractive()
const { $f } = useFormatter()
const ecommerce = useEcommerce()
const {
  cartItems,
  availableCartItems,
  unavailableCartItems,
  selectedProductIds,
  selectedItems,
  allSelected,
  canCheckout,
  toggleSelectAll,
  handleSelectedChange,
} = useCartSelection()

// useCartPricing needs a guaranteed `amount`, whereas the cart DTO keeps it optional
const pricingLines = computed<CartPricingLineInput[]>(() =>
  selectedItems.value.map(item => ({ id: item.id, amount: item.amount || 1 })),
)

const {
  pricing,
  refreshPricing,
  lineUnitPriceByProductId,
  activePromoCode,
  pointsToSpend,
} = useCartPricing(pricingLines)

const promoLoading = ref(false)

const hasSelectedItems = computed(() => selectedItems.value.length > 0)

const {
  inputCode,
  promoCodeError,
  applyPromoCode,
  clearPromoCode,
  hasSavedPromoWithoutSelection,
} = usePromoCode(refreshPricing, hasSelectedItems)

const totalLength = computed(() => store.totalLength)
const isAnonymous = computed(() => Boolean(authStore.session?.isAnon))

const fallbackCost = computed(() =>
  orderService.calculateItemsCost(selectedItems.value),
)

const selectedTotal = computed(() => {
  if (pricing.value) {
    return pricing.value.payable ?? pricing.value.payableBeforePoints
  }

  return fallbackCost.value
})

const showPointsApplied = computed(() =>
  Boolean(pricing.value && (pricing.value.pointsApplied ?? 0) > 0),
)

const showDiscount = computed(() =>
  Boolean(pricing.value && (pricing.value.discountTotal ?? 0) > 0),
)

const showCashbackAccrual = computed(() =>
  Boolean(pricing.value && (pricing.value.cashbackAccrualTotal ?? 0) > 0),
)

async function handleApplyPromo() {
  promoLoading.value = true

  try {
    await applyPromoCode()
  }
  finally {
    promoLoading.value = false
  }
}

async function handleClearPromo() {
  promoLoading.value = true

  try {
    await clearPromoCode()
  }
  finally {
    promoLoading.value = false
  }
}

function handleCheckout() {
  orderSupport.saveItemsForOrder(selectedItems.value)
  navigateTo(orderRouting.create.route)
}

function handleRemove(productId: number) {
  send(() => removeFromCart(productId), {
    key: 'remove',
  })
}

function handleToggleNotify(productId: number) {
  send(() => toggleNotify(productId), {
    key: 'toggleNotify',
    successMessage: 'Статус отслеживания обновлён',
  })
}

async function handleBulkRemove() {
  const ids = [...selectedProductIds.value]
  if (!ids.length) return

  const itemsToRemove = availableCartItems.value.filter(item =>
    ids.includes(item.id!),
  )

  const removed = await send(
    async () => {
      for (const id of ids) {
        await removeFromCart(id)
      }
      return true
    },
    {
      key: 'bulk-remove',
      confirm: {
        message: `Удалить выбранные товары (${ids.length}) из корзины?`,
      },
      successMessage: 'Товары удалены из корзины',
    },
  )

  if (!removed) return

  itemsToRemove.forEach(item =>
    ecommerce.remove(mapCatalogProduct(item, { quantity: item.amount || 1 })),
  )
}
</script>
